import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, Users } from 'lucide-react';
import { callingsApi } from '../../services/api';
import EmptyState from '../shared/EmptyState';
import Badge from '../shared/Badge';
import Modal from '../shared/Modal';
import CallingDetail from './CallingDetail';
import NewCallingModal from './NewCallingModal';
import { formatDateShort } from '../../utils/formatters';
import toast from 'react-hot-toast';
import clsx from 'clsx';

type TabType = 'in_progress' | 'archived';

const WARD_STEP_MAP: Record<string, string> = {
  'Ala Árvore Grande': 'step_sustain_arvore_grande',
  'Ala Barcelona': 'step_sustain_barcelona',
  'Ala Votorantim': 'step_sustain_votorantim',
  'Ala Rio Acima': 'step_sustain_rio_acima',
  'Ala São Roque': 'step_sustain_sao_roque',
  'Ramo Mairinque': 'step_sustain_mairinque',
  'Ramo Piedade': 'step_sustain_piedade',
};

const ALL_SUSTAIN_FIELDS = [
  'step_sustain_arvore_grande',
  'step_sustain_barcelona',
  'step_sustain_votorantim',
  'step_sustain_rio_acima',
  'step_sustain_sao_roque',
  'step_sustain_mairinque',
  'step_sustain_piedade',
];

function getCallingProgress(calling: Record<string, unknown>): { done: number; total: number } {
  const steps: (number | null)[] = [
    calling.step_presidency_approval as number,
    calling.step_bishop_approval as number,
    calling.step_high_council_sustain as number,
    calling.step_interview_by ? 1 : 0,
    getSustainProgress(calling),
    calling.step_lcr_recorded as number,
    calling.type === 'chamado' ? (calling.step_set_apart_by ? 1 : 0) : null,
  ];

  const filtered = steps.filter(s => s !== null) as number[];
  const done = filtered.filter(s => s === 1).length;
  return { done, total: filtered.length };
}

function getSustainProgress(calling: Record<string, unknown>): number {
  if (!calling.step_interview_by) return 0;
  if (calling.sustain_scope === 'ala') {
    const field = WARD_STEP_MAP[calling.ward as string];
    return field && calling[field] ? 1 : 0;
  }
  return ALL_SUSTAIN_FIELDS.every(f => calling[f]) ? 1 : 0;
}

export default function Callings() {
  const [tab, setTab] = useState<TabType>('in_progress');
  const [selectedCalling, setSelectedCalling] = useState<any>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [sustainText, setSustainText] = useState('');
  const [showSustainModal, setShowSustainModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['callings', { status: tab }],
    queryFn: () => callingsApi.list({ status: tab }),
  });

  const callings = (data?.data || []) as unknown as Record<string, unknown>[];

  const generateTextMutation = useMutation({
    mutationFn: () => callingsApi.generateSustainText(callings.map(c => c.id as string)),
    onSuccess: (res) => {
      setSustainText(res.data.text);
      setShowSustainModal(true);
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-4">
        {(['in_progress', 'archived'] as TabType[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={clsx('flex-1 py-2 rounded-lg text-sm font-medium transition-colors',
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}>
            {t === 'in_progress' ? 'Em Andamento' : 'Arquivo'}
          </button>
        ))}
      </div>

      {/* Generate sustain text button */}
      {tab === 'in_progress' && callings.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => generateTextMutation.mutate()}
            disabled={generateTextMutation.isPending}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            <FileText size={14} />
            {generateTextMutation.isPending ? 'Gerando...' : 'Gerar Texto de Apoio'}
          </button>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="card animate-pulse h-24" />)}</div>
      ) : callings.length === 0 ? (
        <EmptyState
          icon={Users}
          title={tab === 'in_progress' ? 'Nenhum chamado em andamento' : 'Arquivo vazio'}
        />
      ) : (
        <div className="space-y-3">
          {callings.map((calling) => {
            const { done, total } = getCallingProgress(calling);
            const progress = total > 0 ? (done / total) * 100 : 0;

            return (
              <div
                key={calling.id as string}
                className="card cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedCalling(calling)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-medium text-gray-800">{calling.person_name as string}</p>
                      <Badge
                        label={calling.type === 'chamado' ? 'Chamado' : 'Desobrigação'}
                        variant={calling.type === 'chamado' ? 'blue' : 'orange'}
                        size="xs"
                      />
                    </div>
                    <p className="text-sm text-gray-600">{calling.calling_name as string}</p>
                    <p className="text-xs text-gray-400">{calling.ward as string}</p>

                    {tab === 'in_progress' && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-400">{done}/{total} etapas</span>
                          <span className="text-xs text-gray-400">{Math.round(progress)}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-500 rounded-full animate-progress"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {tab === 'archived' && !!calling.archived_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        Concluído em {formatDateShort(calling.archived_at as string)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FAB */}
      {tab === 'in_progress' && (
        <button
          onClick={() => setShowNewModal(true)}
          className="fixed bottom-28 right-4 w-14 h-14 bg-brand-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-brand-600 transition-colors z-30"
          aria-label="Novo chamado"
        >
          <Plus size={24} />
        </button>
      )}

      {selectedCalling && (
        <CallingDetail calling={selectedCalling} onClose={() => setSelectedCalling(null)} />
      )}
      <NewCallingModal isOpen={showNewModal} onClose={() => setShowNewModal(false)} />

      {/* Sustain text modal */}
      <Modal
        isOpen={showSustainModal}
        onClose={() => setShowSustainModal(false)}
        title="Texto de Apoio"
        size="lg"
        footer={
          <div className="flex gap-3">
            <button
              onClick={() => navigator.clipboard.writeText(sustainText).then(() => toast.success('Copiado!'))}
              className="btn-secondary flex-1"
            >
              Copiar
            </button>
            <button onClick={() => setShowSustainModal(false)} className="btn-primary flex-1">Fechar</button>
          </div>
        }
      >
        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-4 rounded-lg overflow-x-auto">
          {sustainText}
        </pre>
      </Modal>
    </div>
  );
}
